import type { OrderResponse, OrderService } from '@/types/property.types';

/* Coexistence-only catalog knowledge: legacy fulfillment splits this bundle
   into its component services shortly after order creation. The split is not
   a customer-meaningful change, so both sides of the snapshot comparison are
   normalized through this table. Delete when legacy↔Connect coexistence ends. */
const BUNDLE_COMPONENT_SLUGS: Record<string, string[]> = {
  photos_and_zillow_3d: ['photos', 'zillow_3d_and_floor_plans']
};

/** Everything the Services Ordered cell needs from an order row. */
export type ServicesOrderedSource = Pick<
  OrderResponse,
  'services' | 'createSnapshot'
>;

export interface ServicesOrderedModel {
  /** Current services as ordered (rendered as category pills by the cell). */
  currentServices: OrderService[];
  /** Originally-ordered services from the snapshot, or null without one. */
  originalServices: OrderService[] | null;
  /** Show originalServices struck through beneath the primary line. */
  showOriginal: boolean;
}

function normalizedSlugSet(services: OrderService[]): Set<string> {
  const slugs = services.flatMap(
    (s) => BUNDLE_COMPONENT_SLUGS[s.shoppingCartSlug] ?? [s.shoppingCartSlug]
  );
  return new Set(slugs);
}

export function buildServicesOrderedModel(
  source: ServicesOrderedSource
): ServicesOrderedModel {
  const current = source.services ?? [];
  const original = source.createSnapshot?.services ?? null;

  // Only removals count as a customer-meaningful change. Additions (e.g. trip
  // fees appended by fulfillment) are not ordered by the customer, so the diff
  // is intentionally one-directional: original → current, not symmetric.
  let showOriginal = false;
  if (original) {
    const currentSlugs = normalizedSlugSet(current);
    showOriginal = [...normalizedSlugSet(original)].some(
      (slug) => !currentSlugs.has(slug)
    );
  }

  return {
    currentServices: current,
    originalServices: original,
    showOriginal
  };
}
